import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { type MrpSkuDetail } from "@/lib/mrp";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useHubToast } from "@/components/ui/HubToast";
import { cn } from "@/lib/utils";

export function mrpSkuDetailQueryKey(sku: string): readonly ["mrp", "skus", "detail", string] {
  return ["mrp", "skus", "detail", sku];
}

export function SkuQuoteDeployReviewBanner({ sku }: { sku: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useHubModuleWrite("mrp");

  const detailQuery = useQuery<MrpSkuDetail, ApiError>({
    queryKey: mrpSkuDetailQueryKey(sku),
    queryFn: () => apiFetch<MrpSkuDetail>("/api/mrp/skus", { query: { sku } }),
    enabled: sku.length > 0,
  });

  const clearReviewMutation = useMutation<void, ApiError>({
    mutationFn: async () => {
      await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
        method: "PATCH",
        query: { sku },
        body: { quote_deploy_needs_review: false },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mrpSkuDetailQueryKey(sku) });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      showToast({ tone: "success", message: "Needs review cleared." });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const detail = detailQuery.data;
  if (detail == null || !detail.quote_deploy_needs_review) {
    return null;
  }

  const reasons = detail.quote_deploy_review_reasons ?? [];
  const dismissDisabled = !hubWritesAllowed || clearReviewMutation.isPending;

  return (
    <>
      <span
        className="inline-flex items-center gap-1 rounded-full border-2 border-amber-500 bg-amber-400 pl-3 pr-1 py-1 text-sm font-semibold text-amber-950 shadow-sm"
        role="status"
      >
        Needs review
        <button
          type="button"
          className={cn(
            "inline-flex rounded-full p-1 transition-colors",
            dismissDisabled
              ? "cursor-not-allowed opacity-60"
              : "hover:bg-amber-500/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-amber-800",
          )}
          aria-label="Clear needs review"
          title={hubWritesAllowed ? "Clear needs review" : "Read-only MRP access"}
          disabled={dismissDisabled}
          onClick={() => clearReviewMutation.mutate()}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </span>
      {reasons.length > 0 ? (
        <ul className="w-full basis-full list-disc space-y-0.5 pl-5 text-sm text-amber-900">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
